import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Sparkles, Check, MessageCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const questions = [
  {
    id: 'goal',
    question: "What's your main goal with Sermorelin?",
    options: [
      { value: 'lose_weight', label: 'Lose weight & burn fat', icon: '🏋️' },
      { value: 'build_muscle', label: 'Build muscle & strength', icon: '💪' },
      { value: 'anti_aging', label: 'Anti-aging & longevity', icon: '✨' },
      { value: 'all_above', label: 'All of the above', icon: '🎯' },
    ]
  },
  {
    id: 'age_range',
    question: "How old are you?",
    options: [
      { value: 'under_35', label: 'Under 35' },
      { value: '35_45', label: '35–45' },
      { value: '46_55', label: '46–55' },
      { value: '55_plus', label: '55+' },
    ]
  },
  {
    id: 'current_program',
    question: "Are you currently on any other program?",
    options: [
      { value: 'glp1', label: 'GLP-1 (Semaglutide/Tirzepatide)' },
      { value: 'nad', label: 'NAD+ Therapy' },
      { value: 'both', label: 'Both GLP-1 and NAD+' },
      { value: 'none', label: 'None yet' },
    ]
  }
];

const getRecommendation = (answers) => {
  const { goal, current_program } = answers;
  if (current_program === 'glp1' || current_program === 'both') {
    return "Based on your answers, adding Sermorelin to your GLP-1 program could significantly accelerate your results — especially for muscle preservation and energy.";
  }
  if (goal === 'anti_aging' || current_program === 'nad') {
    return "Sermorelin + NAD+ is one of the most powerful anti-aging combinations available. Bryan can walk you through exactly what this looks like for someone your age.";
  }
  return "You look like a strong candidate for Sermorelin therapy. Bryan personally reviews every inquiry and will reach out quickly.";
};

const SMS_HREF = "sms:+18016518363&body=Hey%20Bryan%2C%20I%20just%20took%20the%20Sermorelin%20quiz%20and%20I%27m%20interested%20in%20learning%20more";

const SermorelinQuizPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const totalSteps = 4;
  const progress = ((step + 1) / totalSteps) * 100;
  const isResults = step === questions.length;

  const handleSelect = (qId, value) => {
    const next = { ...answers, [qId]: value };
    setAnswers(next);
    setTimeout(() => {
      if (step < questions.length - 1) {
        setStep(s => s + 1);
      } else {
        // Submit to backend then show results
        submitLead(next);
        setStep(questions.length);
      }
    }, 300);
  };

  const submitLead = async (data) => {
    try {
      await fetch(`${API_URL}/api/sermorelin-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (_) { /* Lead tracking is non-critical */ }
  };

  const handleBack = () => { if (step > 0) setStep(s => s - 1); };

  // Results screen
  if (isResults) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #020617 0%, #0c1929 50%, #0f172a 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif'
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            maxWidth: '520px', width: '100%', textAlign: 'center',
            padding: '48px 32px',
            background: 'rgba(15, 23, 42, 0.8)',
            borderRadius: '24px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Check style={{ width: '40px', height: '40px', color: 'white' }} />
          </div>

          <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#f8fafc', marginBottom: '16px', lineHeight: 1.3 }}>
            Your Sermorelin Assessment
          </h1>

          <p style={{ fontSize: '16px', color: '#c4b5fd', lineHeight: 1.7, marginBottom: '36px', padding: '0 8px' }}>
            {getRecommendation(answers)}
          </p>

          {/* Primary CTA — Text Bryan */}
          <a
            href={SMS_HREF}
            data-testid="sermorelin-text-bryan-btn"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              width: '100%', padding: '18px 24px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
              border: 'none', borderRadius: '999px',
              color: 'white', fontSize: '18px', fontWeight: 700,
              textDecoration: 'none',
              boxShadow: '0 10px 40px rgba(139, 92, 246, 0.35)',
              transition: 'all 0.2s'
            }}
          >
            <MessageCircle style={{ width: '22px', height: '22px' }} />
            Text Bryan Directly
          </a>

          {/* Secondary CTA */}
          <button
            onClick={() => navigate('/quiz')}
            data-testid="sermorelin-forecast-btn"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              width: '100%', marginTop: '14px', padding: '16px 24px',
              background: 'transparent',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              borderRadius: '999px',
              color: '#94a3b8', fontSize: '15px', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.2s'
            }}
          >
            Get My Full AI Forecast
            <ArrowRight style={{ width: '18px', height: '18px' }} />
          </button>

          <p style={{ fontSize: '12px', color: '#475569', marginTop: '28px', lineHeight: 1.5 }}>
            Results vary. Not medical advice. Individual results may differ.
          </p>
        </motion.div>
      </div>
    );
  }

  // Quiz steps
  const q = questions[step];
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #020617 0%, #0c1929 50%, #0f172a 100%)',
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif'
    }}>
      {/* Header */}
      <header style={{ padding: '20px', borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => step === 0 ? navigate(-1) : handleBack()} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <ChevronLeft style={{ width: '20px', height: '20px' }} /> Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#c4b5fd' }}>
            <Sparkles style={{ width: '20px', height: '20px' }} />
            <span style={{ fontWeight: 700, fontSize: '14px' }}>Sermorelin Assessment</span>
          </div>
          <div style={{ width: '60px' }} />
        </div>
      </header>

      {/* Progress */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 20px 0' }}>
        <div style={{ height: '4px', background: 'rgba(148,163,184,0.2)', borderRadius: '2px', overflow: 'hidden' }}>
          <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} style={{ height: '100%', background: 'linear-gradient(90deg, #8b5cf6 0%, #a855f7 100%)', borderRadius: '2px' }} />
        </div>
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '12px', marginTop: '8px' }}>
          Step {step + 1} of {totalSteps}
        </p>
      </div>

      {/* Question */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '40px 20px' }}>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 32px)', fontWeight: 800, color: '#f8fafc', textAlign: 'center', marginBottom: '40px', lineHeight: 1.3 }}>
              {q.question}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {q.options.map(opt => {
                const selected = answers[q.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(q.id, opt.value)}
                    data-testid={`sermorelin-opt-${opt.value}`}
                    style={{
                      padding: '20px 24px',
                      background: selected
                        ? 'linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(168,85,247,0.2) 100%)'
                        : 'rgba(15,23,42,0.6)',
                      border: selected ? '2px solid rgba(139,92,246,0.5)' : '1px solid rgba(148,163,184,0.2)',
                      borderRadius: '16px',
                      color: selected ? '#c4b5fd' : '#e2e8f0',
                      fontSize: '16px', fontWeight: 600, textAlign: 'left',
                      cursor: 'pointer', transition: 'all 0.2s',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}
                  >
                    <span>{opt.icon ? `${opt.icon}  ${opt.label}` : opt.label}</span>
                    {selected && <Check style={{ width: '20px', height: '20px' }} />}
                  </button>
                );
              })}
            </div>

            {step > 0 && (
              <button onClick={handleBack} style={{ marginTop: '32px', background: 'none', border: 'none', color: '#64748b', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', margin: '32px auto 0' }}>
                <ChevronLeft style={{ width: '16px', height: '16px' }} /> Back
              </button>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <footer style={{ padding: '20px', textAlign: 'center', color: '#475569', fontSize: '12px' }}>
        Results vary. Not medical advice.
      </footer>
    </div>
  );
};

export default SermorelinQuizPage;
